import React from 'react';
import PageSearch from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/pages/components/page-search';
import JsmChangeManagement from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/components/jsm-change-management';
import JsmIncidentManagement from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/components/jsm-incident-management';
import JsmProblemManagement from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/components/jsm-problem-management';
import JsmServiceRequest from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/components/jsm-service-request';
import type { MessageDescriptorV2 } from '@atlassian/jira-intl/src/v2/types.tsx';
import {
	CHANGE_MANAGEMENT_FEATURE,
	INCIDENT_MANAGEMENT_FEATURE,
	PROBLEM_MANAGEMENT_FEATURE,
	SERVICE_REQUEST_FEATURE,
	POST_INCIDENT_REVIEW_FEATURE,
} from '@atlassian/jira-servicedesk-common/src/service-project-features/constants.tsx';
import messages from './messages';
import { UpgradePlanContent } from './upgrade-plan-content';

export const PRACTICE_FEATURES = [
	SERVICE_REQUEST_FEATURE,
	INCIDENT_MANAGEMENT_FEATURE,
	PROBLEM_MANAGEMENT_FEATURE,
	CHANGE_MANAGEMENT_FEATURE,
] as const;

export const getPracticeFeatures = () => [
	SERVICE_REQUEST_FEATURE,
	INCIDENT_MANAGEMENT_FEATURE,
	PROBLEM_MANAGEMENT_FEATURE,
	CHANGE_MANAGEMENT_FEATURE,
	POST_INCIDENT_REVIEW_FEATURE,
];

export const FEATURE_CONFIG: {
	[key: string]: {
		illustration: React.ReactNode;
		title: MessageDescriptorV2;
		description: MessageDescriptorV2;
		learnMoreLink: string;
		inProductHelpArticleId: string;
		descriptionForIPH: MessageDescriptorV2;
		learnMoreLinkText: MessageDescriptorV2;
		learnMoreLinkClickEventName: string;
		detailsTitle?: MessageDescriptorV2;
		details?: MessageDescriptorV2[];
		automationSystemRules?: MessageDescriptorV2[];
		popperText?: React.ReactNode;
		isPremiumFeature?: boolean;
	};
} = {
	[SERVICE_REQUEST_FEATURE]: {
		illustration: <JsmServiceRequest alt="" />,
		title: messages.serviceRequestManagementTitle,
		description: messages.serviceRequestManagementDescription,
		learnMoreLink:
			'https://support.atlassian.com/jira-service-management-cloud/docs/what-are-service-requests/',
		inProductHelpArticleId: 'KQWczajRKJY4szPR8IYSq',
		descriptionForIPH: messages.serviceRequestManagementDescriptionForIph,
		learnMoreLinkText: messages.serviceRequestManagementLearnMore,
		learnMoreLinkClickEventName: 'learnMoreAboutServiceRequest',
		detailsTitle: messages.serviceRequestDetailsTitle,
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		details: [messages.serviceRequestDetail1] as MessageDescriptorV2[],
	},
	[INCIDENT_MANAGEMENT_FEATURE]: {
		illustration: <JsmIncidentManagement alt="" />,
		title: messages.incidentManagementTitle,
		description: messages.incidentManagementDescription,
		learnMoreLink:
			'https://support.atlassian.com/jira-service-management-cloud/docs/what-are-incidents/',
		inProductHelpArticleId: '7FKGuAquLRrlRsNOmESYQp',
		descriptionForIPH: messages.incidentManagementDescriptionForInProductHelp,
		learnMoreLinkText: messages.incidentManagementLearnMore,
		learnMoreLinkClickEventName: 'learnMoreAboutIncidentManagement',
		detailsTitle: messages.incidentManagementDetailsTitle,
		details: [
			messages.incidentManagementDetail1,
			messages.incidentManagementDetail2,
			messages.incidentManagementDetail3,
		],
	},
	[PROBLEM_MANAGEMENT_FEATURE]: {
		illustration: <JsmProblemManagement alt="" />,
		title: messages.problemManagementTitle,
		description: messages.problemManagementDescription,
		learnMoreLink:
			'https://support.atlassian.com/jira-service-management-cloud/docs/what-are-problems/',
		inProductHelpArticleId: '1UdImCpAUfqaJX6qQcVnZ3',
		descriptionForIPH: messages.problemManagementDescriptionForInProductHelp,
		learnMoreLinkText: messages.problemManagementLearnMore,
		learnMoreLinkClickEventName: 'learnMoreAboutProblemManagement',
		detailsTitle: messages.problemManagementDetailsTitle,
		details: [
			messages.problemManagementDetail1,
			messages.problemManagementDetail2,
			messages.problemManagementDetail3,
		],
		popperText: <UpgradePlanContent feature="problemManagement" />,
		isPremiumFeature: true,
	},
	[CHANGE_MANAGEMENT_FEATURE]: {
		illustration: <JsmChangeManagement alt="" />,
		title: messages.changeManagementTitle,
		description: messages.changeManagementDescription,
		learnMoreLink:
			'https://support.atlassian.com/jira-service-management-cloud/docs/what-are-changes/',
		inProductHelpArticleId: '7Mj1hWXgoQdMOcnAFuZGfb',
		descriptionForIPH: messages.changeManagementDescriptionForInProductHelp,
		learnMoreLinkText: messages.changeManagementLearnMore,
		learnMoreLinkClickEventName: 'learnMoreAboutChangeManagement',
		detailsTitle: messages.changeManagementDetailsTitle,
		popperText: <UpgradePlanContent feature="changeManagement" />,
		details: [
			messages.changeManagementDetail1,
			messages.changeManagementDetail2,
			messages.changeManagementDetail3,
		],
		automationSystemRules: [
			messages.changeManagementAutomation1,
			messages.changeManagementAutomation2,
		],
		isPremiumFeature: true,
	},
	[POST_INCIDENT_REVIEW_FEATURE]: {
		illustration: <PageSearch alt="" />,
		title: messages.postIncidentReviewsTitle,
		description: messages.postIncidentReviewsDescription,
		learnMoreLink:
			'https://support.atlassian.com/jira-service-management-cloud/docs/what-are-post-incident-reviews/',
		detailsTitle: undefined,
		details: undefined,
		inProductHelpArticleId: '3e1FLCRCpBoQjPXeBydIx5',
		descriptionForIPH: messages.postIncidentReviewsDescriptionForInProductHelp,
		learnMoreLinkText: messages.learnMoreAboutPostIncidentReviews,
		learnMoreLinkClickEventName: 'learnMoreAboutPostIncidentReviews',
		popperText: <UpgradePlanContent feature="postIncidentReview" />,
		isPremiumFeature: true,
	},
};
